import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { AnimationMode, ModelConfig } from "../../types/models";
import { useModelLoader } from "../../hooks/useModelLoader";
import { useAnimationController } from "../../hooks/useAnimationController";
import { useModelProcessing } from "../../hooks/useModelProcessing";
import {
  bindSceneCamera,
  getBakedCamera,
} from "../../Viewer/animation-v2/CameraSystem";
import { createFrameReporter } from "../../Viewer/animation-v2/FrameReporter";
import { TimelineController } from "../../Viewer/animation-v2/TimelineController";
import { useStepNavigationTimelineBridge } from "../../Viewer/animation-v2/useStepNavigationTimelineBridge";
import { useTimelineRuntime } from "../../Viewer/animation-v2/useTimelineRuntime";
import { StepObjectVisibility, TimelineStep } from "../../types/timeline";
import {
  StepVisibilityInteraction,
  ResolvedVisibilityTarget,
} from "./StepVisibilityInteraction";
import { useStepVisibilityController } from "../../hooks/useStepVisibilityController";

interface DroneModelProps {
  animationMode: AnimationMode;
  config: ModelConfig;
  playing: boolean;
  rangeEndFrame: number | null;
  seekTo: { frame: number; id: number } | null;
  fps: number;
  onFrameUpdate: (frame: number) => void;
  onDurationReady: (durationSeconds: number) => void;
  onRangeComplete: () => void;
  onSeekConsumed?: () => void;
  onBoundsComputed?: (bounds: THREE.Box3, center: THREE.Vector3) => void;
  onModelReady?: () => void;
  onSceneCameraDetected?: (camera: THREE.Camera | null) => void;
  visibilitySteps?: TimelineStep[];
  visibilityCurrentFrame?: number;
  visibilityAssignments?: StepObjectVisibility[];
  enableStepVisibilityEditing?: boolean;
  onVisibilityObjectPick?: (
    event: ResolvedVisibilityTarget & { pointer: { x: number; y: number } },
  ) => void;
  onVisibilityObjectsReady?: (
    objects: Array<{ id: string; label: string }>,
  ) => void;
}

/**
 * DroneModel - thin orchestrator.
 *
 * All logic is delegated to focused hooks and runtime systems:
 *   useModelLoader       - GLB loading, onModelReady
 *   useAnimationController - clip selection and binding
 *   animation-v2 runtime - timeline, frame reporting, camera binding
 *   useModelProcessing   - shadows, materials, bounding box
 */
export function DroneModel({
  animationMode,
  config,
  playing,
  rangeEndFrame,
  seekTo,
  fps,
  onFrameUpdate,
  onDurationReady,
  onRangeComplete,
  onSeekConsumed,
  onBoundsComputed,
  onModelReady,
  onSceneCameraDetected,
  visibilitySteps = [],
  visibilityCurrentFrame = 0,
  visibilityAssignments = [],
  enableStepVisibilityEditing = false,
  onVisibilityObjectPick,
  onVisibilityObjectsReady,
}: DroneModelProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const controllerRef = useRef(new TimelineController(0));

  const { scene, animations, cameras } = useModelLoader({
    config,
    onModelReady,
  });

  const { activeActionRef, activeClipDuration, names } = useAnimationController(
    {
      animations,
      groupRef,
      animationMode,
      onDurationReady,
    },
  );

  const cameraBinding = useMemo(
    () => bindSceneCamera(cameras, animations),
    [animations, cameras],
  );

  const reporter = useMemo(
    () => createFrameReporter(fps, onFrameUpdate),
    [fps, onFrameUpdate],
  );

  useEffect(() => {
    const sceneCamera = getBakedCamera(cameraBinding);
    onSceneCameraDetected?.(sceneCamera);
  }, [cameraBinding, onSceneCameraDetected]);

  useStepNavigationTimelineBridge({
    controller: controllerRef.current,
    clipDuration: activeClipDuration,
    playing,
    seekTo,
    rangeEndFrame,
    fps,
    onSeekConsumed,
  });

  useTimelineRuntime({
    controller: controllerRef.current,
    mixer: null,
    getMixer: () => activeActionRef.current?.getMixer() ?? null,
    prepareForApply: () => {
      const action = activeActionRef.current;
      if (!action) return;
      if (!action.enabled) {
        action.enabled = true;
      }
      if (action.paused) {
        action.paused = false;
      }
    },
    reporter,
    onRangeComplete,
  });

  useModelProcessing(scene, onBoundsComputed, names);
  useStepVisibilityController({
    scene,
    steps: visibilitySteps,
    currentFrame: visibilityCurrentFrame,
    assignments: visibilityAssignments,
    mode: enableStepVisibilityEditing ? "editing" : "playback",
  });

  useEffect(() => {
    if (!scene) return;

    const seenIds = new Set<string>();
    const objects: Array<{ id: string; label: string }> = [];

    scene.traverse((object) => {
      if (!object.parent) return;
      if ((object as THREE.Camera).isCamera) return;
      if ((object as THREE.Light).isLight) return;
      if (object.type === "Bone") return;

      const id = object.name || object.uuid;
      if (!id || seenIds.has(id)) return;
      seenIds.add(id);
      objects.push({ id, label: object.name || object.type });
    });

    onVisibilityObjectsReady?.(objects);
  }, [onVisibilityObjectsReady, scene]);

  return scene ? (
    <>
      <primitive ref={groupRef} object={scene} data-oid="fpk:4rc" />
      {onVisibilityObjectPick ? (
        <StepVisibilityInteraction
          enabled={enableStepVisibilityEditing}
          sceneRoot={scene}
          onPick={onVisibilityObjectPick}
          data-oid="kqbbyue"
        />
      ) : null}
    </>
  ) : null;
}
